import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { WAITLIST_PATH } from "../../../constants";
import { FeatureFlag } from "../../../constants/featureFlags";
import { useAuth } from "../../../contexts/AuthContext";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { useSpecialties } from "../../../hooks/useSpecialties";
import { createPublicAPI } from "../../../services/apiService";
import { AuthService } from "../../../services/authService";
import type { SignUpFormData } from "../../../types/auth";
import { getErrorMessage } from "../../../utils";
import {
  validateEmail,
  validateName,
  validateOrganizationName,
  validatePassword,
} from "../../../utils/authValidation";
import { AuthLayout } from "../AuthLayout";
import { type FormErrors, SignUpView } from "./SignUpView";

export const SignUpRoute = () => {
  const location = useLocation();
  const signupEnabled = useFeatureFlag(FeatureFlag.SignupEnabled);
  if (!signupEnabled) {
    return <Navigate to={`${WAITLIST_PATH}${location.search}`} replace />;
  }
  return <SignUp />;
};

export const SignUp = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [isProfessional, setIsProfessional] = useState(false);
  const [specialtyId, setSpecialtyId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const navigate = useNavigate();
  const { user, signIn } = useAuth();
  const { specialties, isError: specialtiesError } = useSpecialties();

  useEffect(() => {
    if (specialtiesError) {
      toast.error("Failed to load specialties. Please contact support if problem persists.");
    }
  }, [specialtiesError]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const emailError = validateEmail(email);
    if (emailError) newErrors.email = emailError;

    const passwordError = validatePassword(password);
    if (passwordError) newErrors.password = passwordError;

    const nameError = validateName(name);
    if (nameError) newErrors.name = nameError;

    const organizationNameError = validateOrganizationName(organizationName);
    if (organizationNameError) newErrors.organizationName = organizationNameError;

    if (isProfessional && !specialtyId) {
      newErrors.specialty = "Please select a specialty";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const loginUserWithSupabase = async (email: string, password: string) => {
    const response = await signIn({ email, password });
    if (response.error) {
      if (response.error.message === "Email not confirmed") {
        navigate("/email-confirmation", { replace: true, state: { email } });
      }
      throw response.error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const publicApi = createPublicAPI("v2");
      const authService = new AuthService(publicApi);

      const signupData: SignUpFormData = {
        email,
        password,
        name,
        organization_name: organizationName,
        is_professional: isProfessional,
        ...(isProfessional ? { specialty_id: specialtyId } : {}),
      };

      await authService.signup(signupData);
      toast.success("Sign up successful! Please check your email to confirm your account.");
      await loginUserWithSupabase(email, password);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (user) {
    return <Navigate to="/cases" replace />;
  }

  return (
    <AuthLayout>
      <SignUpView
        email={email}
        password={password}
        name={name}
        organizationName={organizationName}
        isProfessional={isProfessional}
        specialtyId={specialtyId}
        specialties={specialties}
        isSubmitting={isSubmitting}
        errors={errors}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onNameChange={setName}
        onOrganizationNameChange={setOrganizationName}
        onIsProfessionalChange={setIsProfessional}
        onSpecialtyIdChange={setSpecialtyId}
        onSubmit={handleSubmit}
      />
    </AuthLayout>
  );
};
